import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  LIBELLES_MOTIF_SORTIE,
  LIBELLES_STATUT_EMPLOYE,
  MOTIFS_SORTIE,
  anciennete,
  auMoins,
  jourDeTunis,
  type EmployeDetail,
  type ModificationEmploye,
  type MotifSortie,
} from '@rh/shared';
import { appel } from '../api';
import { useAuth } from '../auth/contexte-auth';
import type { PointDeVente } from '../composants/Embaucher';
import { Chargement, Erreur } from '../composants/Etat';
import { Note, Pastille } from '../composants/Pastille';
import { nomComplet } from '../format';

function jourLisible(jour: string): string {
  return jour.split('-').reverse().join('/');
}

/** Fiche employé : affectation, antécédent de recrutement, sortie. */
export function FicheEmploye() {
  const { id = '' } = useParams();
  const { utilisateur } = useAuth();
  const [fiche, setFiche] = useState<EmployeDetail | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    appel<EmployeDetail>(`/employes/${id}`)
      .then(setFiche)
      .catch((e: Error) => setErreur(e.message));
  }, [id]);

  if (erreur && !fiche) return <Erreur message={erreur} />;
  if (!fiche) return <Chargement quoi="de la fiche employé" />;

  const rh = utilisateur ? auMoins(utilisateur.role, 'rh') : false;
  const enPoste = fiche.statut === 'actif';

  return (
    <article className="fiche fiche-employe">
      <header className="bandeau">
        <div>
          <h2>{nomComplet(fiche)}</h2>
          <p className="meta">
            {fiche.matricule && <code>{fiche.matricule}</code>}
            <span className={`statut-annonce statut-employe-${fiche.statut}`}>
              {LIBELLES_STATUT_EMPLOYE[fiche.statut]}
            </span>
          </p>
        </div>
        {fiche.telephone && (
          <a className="appeler" href={`tel:${fiche.telephone}`}>
            {fiche.telephone}
          </a>
        )}
      </header>

      <section className="bloc">
        <h3>Poste</h3>
        <dl className="proprietes">
          <div><dt>Poste</dt><dd>{fiche.poste ?? '—'}</dd></div>
          <div><dt>Point de vente</dt><dd>{fiche.pointDeVente ?? '—'}</dd></div>
          <div><dt>Métier</dt><dd>{fiche.famille ?? '—'}</dd></div>
          <div><dt>Entrée</dt><dd>{jourLisible(fiche.dateEntree)}</dd></div>
          <div>
            <dt>Ancienneté</dt>
            <dd>{anciennete(fiche.dateEntree, fiche.dateSortie ?? jourDeTunis())}</dd>
          </div>
          {fiche.email && <div><dt>E-mail</dt><dd>{fiche.email}</dd></div>}
          {(fiche.adresse || fiche.ville) && (
            <div><dt>Adresse</dt><dd>{[fiche.adresse, fiche.ville].filter(Boolean).join(', ')}</dd></div>
          )}
        </dl>
        {rh && enPoste && <ModifierAffectation fiche={fiche} onChange={setFiche} />}
      </section>

      <section className="bloc">
        <h3>Recrutement</h3>
        {fiche.candidatureId ? (
          <p className="antecedent">
            <Pastille valeur={fiche.potentiel} />
            <span>
              <Link to={`/candidatures/${fiche.candidatureId}`}>
                {fiche.annonceIntitule ?? 'Candidature d’origine'}
              </Link>
              {fiche.annonceReference && <code>{fiche.annonceReference}</code>}
            </span>
            <Note valeur={fiche.noteEntretien} date={null} />
          </p>
        ) : (
          <p className="mention">Fiche créée sans candidature d’origine.</p>
        )}
      </section>

      <section className="bloc">
        <h3>Sortie</h3>
        {fiche.dateSortie ? (
          <dl className="proprietes">
            <div><dt>Date</dt><dd>{jourLisible(fiche.dateSortie)}</dd></div>
            <div><dt>Motif</dt><dd>{fiche.motifSortie ? LIBELLES_MOTIF_SORTIE[fiche.motifSortie] : '—'}</dd></div>
            {fiche.commentaireSortie && <div><dt>Précision</dt><dd>{fiche.commentaireSortie}</dd></div>}
          </dl>
        ) : rh ? (
          <EnregistrerSortie fiche={fiche} onChange={setFiche} />
        ) : (
          <p className="mention">En poste.</p>
        )}
      </section>
    </article>
  );
}

function ModifierAffectation({ fiche, onChange }: { fiche: EmployeDetail; onChange: (f: EmployeDetail) => void }) {
  const [ouvert, setOuvert] = useState(false);
  const [points, setPoints] = useState<PointDeVente[]>([]);
  const [saisie, setSaisie] = useState<ModificationEmploye>({
    poste: fiche.poste ?? '',
    pointDeVenteId: fiche.pointDeVenteId ?? '',
    matricule: fiche.matricule ?? '',
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!ouvert) return;
    appel<PointDeVente[]>('/points-de-vente')
      .then(setPoints)
      .catch((e: Error) => setErreur(e.message));
  }, [ouvert]);

  if (!ouvert) {
    return (
      <div className="actions-fiche">
        <button type="button" className="bouton-second" onClick={() => setOuvert(true)}>
          Corriger le poste ou l’affectation
        </button>
      </div>
    );
  }

  async function valider(): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    try {
      const corrigee = await appel<EmployeDetail>(`/employes/${fiche.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...saisie, matricule: saisie.matricule?.trim() || null }),
      });
      onChange(corrigee);
      setOuvert(false);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Correction impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <form
      className="formulaire-employe"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        void valider();
      }}
    >
      <label className="champ">
        Poste
        <input
          type="text"
          required
          minLength={2}
          maxLength={120}
          value={saisie.poste}
          onChange={(e) => setSaisie({ ...saisie, poste: e.target.value })}
        />
      </label>
      <label className="champ">
        Point de vente
        <select
          required
          value={saisie.pointDeVenteId}
          onChange={(e) => setSaisie({ ...saisie, pointDeVenteId: e.target.value })}
        >
          <option value="">Choisir…</option>
          {points.map((point) => (
            <option key={point.id} value={point.id}>
              {point.libelle}
            </option>
          ))}
        </select>
      </label>
      <label className="champ">
        Matricule
        <input
          type="text"
          maxLength={30}
          value={saisie.matricule ?? ''}
          onChange={(e) => setSaisie({ ...saisie, matricule: e.target.value })}
        />
      </label>
      {erreur && <Erreur message={erreur} />}
      <div className="actions">
        <button type="button" className="lien" onClick={() => setOuvert(false)}>
          Annuler
        </button>
        <button type="submit" className="bouton" disabled={envoi}>
          Enregistrer
        </button>
      </div>
    </form>
  );
}

function EnregistrerSortie({ fiche, onChange }: { fiche: EmployeDetail; onChange: (f: EmployeDetail) => void }) {
  const [ouvert, setOuvert] = useState(false);
  const [date, setDate] = useState(jourDeTunis());
  const [motif, setMotif] = useState<MotifSortie | ''>('');
  const [commentaire, setCommentaire] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  if (!ouvert) {
    return (
      <button type="button" className="bouton-second" onClick={() => setOuvert(true)}>
        Enregistrer une sortie
      </button>
    );
  }

  async function valider(): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    try {
      const sortie = await appel<EmployeDetail>(`/employes/${fiche.id}/sortie`, {
        method: 'POST',
        body: JSON.stringify({ date, motif, commentaire: commentaire.trim() || null }),
      });
      onChange(sortie);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Sortie non enregistrée');
      setEnvoi(false);
    }
  }

  return (
    <form
      className="formulaire-employe"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        void valider();
      }}
    >
      <label className="champ">
        Date de sortie
        <input type="date" required min={fiche.dateEntree} value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label className="champ">
        Motif
        <select required value={motif} onChange={(e) => setMotif(e.target.value as MotifSortie | '')}>
          <option value="">Choisir…</option>
          {MOTIFS_SORTIE.map((m) => (
            <option key={m} value={m}>
              {LIBELLES_MOTIF_SORTIE[m]}
            </option>
          ))}
        </select>
      </label>
      <label className="champ">
        Précision {motif === 'autre' ? '(obligatoire)' : '(facultative)'}
        <input
          type="text"
          maxLength={500}
          required={motif === 'autre'}
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
        />
      </label>
      <p className="mention">La sortie ferme la fiche : elle ne se corrige plus ensuite.</p>
      {erreur && <Erreur message={erreur} />}
      <div className="actions">
        <button type="button" className="lien" onClick={() => setOuvert(false)}>
          Annuler
        </button>
        <button type="submit" className="bouton" disabled={envoi || !motif}>
          Enregistrer la sortie
        </button>
      </div>
    </form>
  );
}
